import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, Switch, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  USER_STATUS_LABEL,
  USER_STATUS_OPTIONS,
  createGiftOrder,
  queryAdminUsers,
  updateUserAccountStatus,
  validateUserQuery,
  type AdminUserRecord,
  type UserAccountStatus,
  type UserQuery,
  type UserStatusCode
} from './userManageData';
import { UserDetailModal } from './UserDetailModal';
import { getGiftablePlans, getPlanOptions, type GiftablePlan, type PlanOption } from '../packages/packageManageData';
import { REGION_OPTIONS } from '../packages/packageFormOptions';

type DraftFilters = {
  userCode: string;
  displayName: string;
  phone: string;
  email: string;
  status?: UserStatusCode;
  planCode: string;
  regionCode: string;
};

const USER_CODE_PATTERN = /^[0-9]{21}$/;

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `gift-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function toQuery(draft: Partial<DraftFilters>): Omit<UserQuery, 'pageNum' | 'pageSize'> {
  return {
    userCode: draft.userCode?.trim() ?? '',
    displayName: draft.displayName?.trim() ?? '',
    phone: draft.phone?.trim() ?? '',
    email: draft.email?.trim() ?? '',
    status: draft.status,
    planCode: draft.planCode?.trim() ?? '',
    regionCode: draft.regionCode?.trim().toUpperCase() ?? ''
  };
}

function statusClass(status: string) {
  if (status === 'NORMAL') return 'normal';
  if (status === 'EXPIRING') return 'expiring';
  if (status === 'DISABLED') return 'disabled';
  return 'normal';
}

function statusLabel(status: string) {
  if (status === 'NORMAL' || status === 'EXPIRING' || status === 'DISABLED') {
    return USER_STATUS_LABEL[status];
  }
  return status || '-';
}

export function UserManage() {
  const [form] = Form.useForm<DraftFilters>();
  const [query, setQuery] = useState<Omit<UserQuery, 'pageNum' | 'pageSize'>>({});
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState<AdminUserRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailCode, setDetailCode] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [statusTarget, setStatusTarget] = useState<{
    userCode: string;
    name: string;
    status: UserAccountStatus;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [giftTarget, setGiftTarget] = useState<{ userCode: string; name: string } | null>(null);
  const [giftPlanCode, setGiftPlanCode] = useState<string>();
  const [giftDays, setGiftDays] = useState<number | null>(null);
  const [giftReason, setGiftReason] = useState('');
  const [giftSubmitting, setGiftSubmitting] = useState(false);
  const giftAttempt = useRef({ payload: '', key: '' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await queryAdminUsers({ ...query, pageNum, pageSize });
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || '用户列表加载失败');
          setRows([]);
          setTotal(0);
          return;
        }
        setRows(res.list);
        setTotal(res.total);
      } catch {
        if (!cancelled) message.error('用户列表加载失败，请稍后重试');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [query, pageNum, pageSize, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    const loadPlans = async () => {
      try {
        const res = await getPlanOptions();
        if (cancelled) return;
        if (res.code !== 200 || !Array.isArray(res.content)) {
          if (res.code !== -1) message.error(res.message || '套餐列表加载失败');
          setPlans([]);
          return;
        }
        setPlans(res.content);
      } catch {
        if (!cancelled) {
          message.error('套餐列表加载失败');
          setPlans([]);
        }
      }
    };
    loadPlans();
    return () => {
      cancelled = true;
    };
  }, []);

  const [giftablePlans, setGiftablePlans] = useState<GiftablePlan[]>([]);

  useEffect(() => {
    let cancelled = false;
    const loadGiftablePlans = async () => {
      const collected: GiftablePlan[] = [];
      try {
        for (let page = 1; page <= 10; page += 1) {
          const res = await getGiftablePlans({ pageNum: page, pageSize: 100 });
          if (cancelled) return;
          const records = res.content?.records;
          if (res.code !== 200 || !Array.isArray(records)) {
            if (res.code !== -1) message.error(res.message || '可赠送套餐加载失败');
            break;
          }
          collected.push(...records);
          if (records.length < 100) break;
        }
        if (!cancelled) setGiftablePlans(collected);
      } catch {
        if (!cancelled) message.error('可赠送套餐加载失败');
      }
    };
    loadGiftablePlans();
    return () => {
      cancelled = true;
    };
  }, []);

  const planOptions = plans
    .filter((plan) => USER_CODE_PATTERN.test(plan.planCode?.trim() ?? ''))
    .map((plan) => ({
      value: plan.planCode?.trim() || '',
      label: plan.displayName?.trim() || plan.planCode?.trim() || ''
    }));

  const giftPlanOptions = giftablePlans
    .filter((plan) => USER_CODE_PATTERN.test(plan.planCode?.trim() ?? ''))
    .map((plan) => ({
      value: plan.planCode?.trim() || '',
      label: plan.displayName?.trim() || plan.planCode?.trim() || ''
    }));

  const search = (values: DraftFilters) => {
    const next = toQuery(values);
    const error = validateUserQuery(next);
    if (error) {
      message.warning(error);
      return;
    }
    setPageNum(1);
    setQuery(next);
  };

  const reset = () => {
    form.resetFields();
    setPageNum(1);
    setQuery({});
  };

  const openStatus = (row: AdminUserRecord) => {
    const userCode = row.userCode.trim();
    if (!USER_CODE_PATTERN.test(userCode)) {
      message.warning('用户编码须为 21 位数字');
      return;
    }
    setReason('');
    setStatusTarget({
      userCode,
      name: row.displayName.trim() || userCode,
      status: row.status === 'DISABLED' ? 'ACTIVE' : 'DISABLED'
    });
  };

  const submitStatus = async () => {
    if (!statusTarget || statusSubmitting) return;
    const text = reason.trim();
    if (!text) {
      message.warning('请输入操作原因');
      return;
    }
    if (text.length > 500) {
      message.warning('操作原因最多 500 字');
      return;
    }
    setStatusSubmitting(true);
    try {
      const res = await updateUserAccountStatus(statusTarget.userCode, {
        status: statusTarget.status,
        reason: text
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '修改账号状态失败');
        return;
      }
      message.success(res.message || (statusTarget.status === 'ACTIVE' ? '已启用' : '已停用'));
      setStatusTarget(null);
      setReason('');
      setReloadKey((value) => value + 1);
    } catch {
      message.error('修改账号状态失败');
    } finally {
      setStatusSubmitting(false);
    }
  };

  const openGift = (row: AdminUserRecord) => {
    const userCode = row.userCode.trim();
    if (!USER_CODE_PATTERN.test(userCode)) {
      message.warning('用户编码须为 21 位数字');
      return;
    }
    setGiftPlanCode(undefined);
    setGiftDays(null);
    setGiftReason('');
    giftAttempt.current = { payload: '', key: '' };
    setGiftTarget({ userCode, name: row.displayName.trim() || userCode });
  };

  const submitGift = async () => {
    if (!giftTarget || giftSubmitting) return;
    const planCode = giftPlanCode?.trim() ?? '';
    const days = giftDays ?? 0;
    const text = giftReason.trim();
    if (!USER_CODE_PATTERN.test(planCode)) {
      message.warning('请选择套餐');
      return;
    }
    if (!Number.isInteger(days) || days < 1) {
      message.warning('赠送天数须为大于 0 的整数');
      return;
    }
    if (!text) {
      message.warning('请输入赠送原因');
      return;
    }
    if (text.length > 500) {
      message.warning('赠送原因最多 500 字');
      return;
    }
    const payload = `${giftTarget.userCode}|${planCode}|${days}|${text}`;
    if (giftAttempt.current.payload !== payload || !giftAttempt.current.key) {
      giftAttempt.current = { payload, key: createIdempotencyKey() };
    }
    setGiftSubmitting(true);
    try {
      const res = await createGiftOrder({
        userCode: giftTarget.userCode,
        planCode,
        durationDays: days,
        reason: text,
        idempotencyKey: giftAttempt.current.key
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '赠送套餐失败');
        return;
      }
      const orderNo = res.content?.orderNo?.trim() ?? '';
      const tip = res.message || '已赠送';
      message.success(orderNo && !tip.includes(orderNo) ? `${tip}（订单号 ${orderNo}）` : tip);
      setGiftTarget(null);
      giftAttempt.current = { payload: '', key: '' };
      setReloadKey((value) => value + 1);
    } catch {
      message.error('赠送套餐失败');
    } finally {
      setGiftSubmitting(false);
    }
  };

  const columns: ColumnsType<AdminUserRecord> = [
    {
      title: '用户编码',
      dataIndex: 'userCode',
      width: 200,
      render: (value: string) => <div className='admin-cell-primary'>{value || '-'}</div>
    },
    {
      title: '用户名',
      dataIndex: 'displayName',
      width: 140,
      render: (value: string) => <div className='admin-cell-primary'>{value || '-'}</div>
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      width: 220,
      render: (value: string) => <div className='admin-cell-primary admin-cell-email'>{value || '-'}</div>
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      width: 160,
      render: (value: string) => <div className='admin-cell-primary'>{value || '-'}</div>
    },
    {
      title: '地区',
      dataIndex: 'regionCode',
      width: 80,
      render: (value: string) => <div className='admin-cell-primary'>{value || '-'}</div>
    },
    {
      title: '套餐',
      dataIndex: 'planCode',
      width: 180,
      render: (_, row) => <div className='admin-cell-primary'>{row.planName || row.planCode || '-'}</div>
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 140,
      render: (status: string) => (
        <div className={`admin-status admin-status--${statusClass(status)}`}>
          <span className='admin-status-dot' />
          {statusLabel(status)}
        </div>
      )
    },
    {
      title: '注册时间',
      dataIndex: 'registeredAt',
      width: 200,
      render: (value: string) => <div className='admin-cell-primary'>{value || '-'}</div>
    },
    {
      title: '过期时间',
      dataIndex: 'membershipExpiresAt',
      width: 200,
      render: (value: string) => <div className='admin-cell-primary'>{value || '-'}</div>
    },
    {
      title: '是否启用',
      dataIndex: 'status',
      width: 90,
      align: 'center',
      render: (status: string, row) => (
        <Switch size='small' checked={status !== 'DISABLED'} onChange={() => openStatus(row)} />
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, row) => (
        <div className='admin-row-actions'>
          <button
            type='button'
            className='admin-link-btn'
            onClick={() => {
              if (!row.userCode) {
                message.warning('缺少用户编码');
                return;
              }
              setDetailCode(row.userCode);
            }}
          >
            详情
          </button>
          <button
            type='button'
            className='admin-link-btn'
            onClick={() => openGift(row)}
          >
            赠送套餐
          </button>
        </div>
      )
    }
  ];

  return (
    <div className='admin-page-card'>
      <Form form={form} layout='horizontal' labelAlign='right' colon className='admin-order-filter' onFinish={search}>
        <div className='admin-order-filter-grid'>
          <Form.Item name='userCode' label='用户编码'>
            <Input allowClear placeholder='21 位用户编码' maxLength={21} />
          </Form.Item>
          <Form.Item name='displayName' label='昵称'>
            <Input allowClear placeholder='请输入昵称' maxLength={128} />
          </Form.Item>
          <Form.Item name='phone' label='手机号'>
            <Input allowClear placeholder='请输入手机号' maxLength={16} />
          </Form.Item>
          <Form.Item name='email' label='邮箱'>
            <Input allowClear placeholder='请输入邮箱' maxLength={320} />
          </Form.Item>
          <Form.Item name='status' label='状态'>
            <Select allowClear placeholder='全部' options={USER_STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item name='planCode' label='套餐'>
            <Select allowClear showSearch optionFilterProp='label' placeholder='全部' options={planOptions} />
          </Form.Item>
          <Form.Item name='regionCode' label='地区'>
            <Select allowClear placeholder='全部' options={REGION_OPTIONS} />
          </Form.Item>
        </div>
        <div className='admin-order-filter-actions'>
          <Button onClick={reset}>重置</Button>
          <Button type='primary' htmlType='submit' className='admin-pkg-query-btn'>
            查询
          </Button>
        </div>
      </Form>

      <Table<AdminUserRecord>
        rowKey='key'
        columns={columns}
        dataSource={rows}
        loading={loading}
        scroll={{ x: 1560 }}
        className='admin-user-table'
        size='middle'
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showQuickJumper: true,
          showTotal: (count) => {
            const pages = Math.max(1, Math.ceil(count / pageSize));
            return `共${pages}页`;
          },
          onChange: (current, size) => {
            setPageNum(current);
            setPageSize(size);
          }
        }}
      />
      <UserDetailModal
        key={`${detailCode ?? ''}-${reloadKey}`}
        open={Boolean(detailCode)}
        userCode={detailCode}
        onClose={() => setDetailCode(null)}
      />
      <Modal
        title={statusTarget?.status === 'ACTIVE' ? '启用用户' : '停用用户'}
        open={Boolean(statusTarget)}
        onCancel={() => {
          if (!statusSubmitting) setStatusTarget(null);
        }}
        destroyOnClose
        centered
        footer={
          <div className='flex justify-end gap-2'>
            <Button className='rounded-lg' disabled={statusSubmitting} onClick={() => setStatusTarget(null)}>
              取消
            </Button>
            <Button type='primary' className='rounded-lg' loading={statusSubmitting} onClick={submitStatus}>
              确认
            </Button>
          </div>
        }
      >
        <p className='mb-3 text-sm text-slate-600'>
          {statusTarget?.status === 'ACTIVE'
            ? `确定启用用户「${statusTarget.name}」吗？启用后该用户须重新登录。`
            : `确定停用用户「${statusTarget?.name ?? ''}」吗？停用后不冻结会员期限，也不调整权益额度。`}
        </p>
        <Input.TextArea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder='请输入操作原因'
          maxLength={500}
          showCount
          rows={4}
        />
      </Modal>
      <Modal
        title='赠送套餐'
        open={Boolean(giftTarget)}
        onCancel={() => {
          if (!giftSubmitting) setGiftTarget(null);
        }}
        destroyOnClose
        centered
        footer={
          <div className='flex justify-end gap-2'>
            <Button className='rounded-lg' disabled={giftSubmitting} onClick={() => setGiftTarget(null)}>
              取消
            </Button>
            <Button type='primary' className='rounded-lg' loading={giftSubmitting} onClick={submitGift}>
              确认
            </Button>
          </div>
        }
      >
        <p className='mb-3 text-sm text-slate-600'>
          赠送给「{giftTarget?.name ?? ''}」。同一套餐会延长有效期，更高等级会立即升级。
        </p>
        <div className='mb-3'>
          <div className='mb-1 text-sm'>套餐</div>
          <Select
            className='w-full'
            showSearch
            allowClear
            optionFilterProp='label'
            placeholder='请选择套餐'
            options={giftPlanOptions}
            value={giftPlanCode}
            onChange={setGiftPlanCode}
            notFoundContent='暂无可选套餐'
          />
        </div>
        <div className='mb-3'>
          <div className='mb-1 text-sm'>有效天数</div>
          <InputNumber
            className='w-full'
            min={1}
            precision={0}
            placeholder='请输入赠送天数'
            value={giftDays}
            onChange={setGiftDays}
          />
        </div>
        <div>
          <div className='mb-1 text-sm'>赠送原因</div>
          <Input.TextArea
            value={giftReason}
            onChange={(event) => setGiftReason(event.target.value)}
            placeholder='请输入赠送原因'
            maxLength={500}
            showCount
            rows={4}
          />
        </div>
      </Modal>
    </div>
  );
}
